import BChatItem from "components/base/BChatItem/BChatItem";
import { observer } from "mobx-react";
import { useCallback, useLayoutEffect, useRef } from "react";
import { chatStore } from "store/chatStore";
import { MessageRole } from "types/message";
import MessageItem from "./MessageItem";
import "./MessageList.scss";

const NEAR_BOTTOM_PX = 120;

const MessageList = observer(function MessageList() {
  const { messages, isAiResponding, streamingMessageId, isLoadingMessages } = chatStore;
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const prevLengthRef = useRef(messages.length);

  const lastMessage = messages[messages.length - 1];
  const lastContent = lastMessage?.content ?? "";

  const isNearBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      return true;
    }
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    stickToBottomRef.current = isNearBottom();
  }, [isNearBottom]);

  useLayoutEffect(() => {
    const lengthIncreased = messages.length > prevLengthRef.current;
    prevLengthRef.current = messages.length;

    if (lengthIncreased || isAiResponding) {
      stickToBottomRef.current = true;
    }

    if (stickToBottomRef.current || lengthIncreased || isAiResponding) {
      scrollToBottom();
    }
  }, [messages.length, lastContent, isAiResponding, isLoadingMessages, scrollToBottom]);

  if (isLoadingMessages && messages.length === 0) {
    return (
      <div className="message-list" ref={listRef} onScroll={handleScroll}>
        <div className="message-list__inner">
          <BChatItem role="assistant" status="loading" />
        </div>
      </div>
    );
  }

  return (
    <div className="message-list" ref={listRef} onScroll={handleScroll}>
      <div className="message-list__inner">
        {messages.map((message) => {
          const isStreaming =
            message.id === streamingMessageId && message.role === MessageRole.Assistant;
          return (
            <MessageItem
              key={message.id}
              message={message}
              status={isStreaming ? "streaming" : "done"}
            />
          );
        })}
        {isAiResponding && streamingMessageId == null ? (
          <BChatItem role="assistant" status="loading" />
        ) : null}
        <div className="message-list__anchor" aria-hidden />
      </div>
    </div>
  );
});

export default MessageList;
